import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  Badge,
  Fab,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QueueIcon from "@mui/icons-material/Queue";

function statusColor(status) {
  if (status === "completed") return "success";
  if (status === "error") return "error";
  if (status === "in_progress") return "info";
  return "warning"; // queued
}

function statusLabel(status) {
  if (status === "completed") return "Done";
  if (status === "error") return "Error";
  if (status === "in_progress") return "Running";
  return "Queued";
}

function ElapsedTimer({ startedAt, status }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt || status === "completed" || status === "error") return;
    const tick = () => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt, status]);

  if (!startedAt) return null;
  if (status === "completed" || status === "error") {
    const secs = Math.floor((Date.now() - startedAt) / 1000);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return (
      <Typography variant="caption" color="text.secondary">
        {m}m {s}s
      </Typography>
    );
  }

  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return (
    <Typography variant="caption" color="text.secondary">
      {m}m {s}s
    </Typography>
  );
}

export default function JobQueueDrawer({ jobs, selectedJobId, onSelectJob }) {
  const [open, setOpen] = useState(false);

  const activeCount = jobs.filter(
    (j) => j.status !== "completed" && j.status !== "error"
  ).length;

  return (
    <>
      {/* Floating badge button */}
      <Tooltip title="Active analyses" placement="left">
        <Fab
          size="medium"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "primary.main",
            "&:hover": { bgcolor: "background.paper", opacity: 0.85 },
            zIndex: 1200,
          }}
        >
          <Badge
            badgeContent={activeCount}
            color="primary"
            invisible={activeCount === 0}
          >
            <QueueIcon sx={{ color: "primary.main" }} />
          </Badge>
        </Fab>
      </Tooltip>

      {/* Slide-in drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            bgcolor: "background.paper",
            borderLeft: "1px solid",
            borderColor: "primary.main",
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1.5}
          sx={{ borderBottom: "1px solid rgba(0,255,0,0.15)" }}
        >
          <Typography variant="h6" color="primary">
            Analysis Queue
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {jobs.length === 0 ? (
          <Box px={2} py={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No analyses submitted yet.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {[...jobs].reverse().map((job, idx) => (
              <React.Fragment key={job.sessionId || idx}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={job.sessionId === selectedJobId}
                    onClick={() => {
                      onSelectJob(job);
                      setOpen(false);
                    }}
                    sx={{
                      "&.Mui-selected": {
                        bgcolor: "rgba(0,255,0,0.08)",
                        "&:hover": { bgcolor: "rgba(0,255,0,0.12)" },
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            {job.ticker}
                          </Typography>
                          <Chip
                            label={statusLabel(job.status)}
                            color={statusColor(job.status)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box display="flex" justifyContent="space-between" mt={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            {job.analysisDate || ""}
                          </Typography>
                          <ElapsedTimer
                            startedAt={job.startedAt}
                            status={job.status}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {idx < jobs.length - 1 && (
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Drawer>
    </>
  );
}
